import React, { FC, memo, useContext, useMemo } from 'react'
import { MapCadastralPlotFragment, MapAuctionFragment } from '@foros-fe/graphql'
import Map from '.'
import { CadastralPlotsGroup } from './Layers/CadastralPlotsGroup'
import { DeflateLayer, AuctionProperties } from './Layers/DeflateLayer'
import { FitMap } from './FitMap'
import AuctionListContext from '../../contexts/AuctionList'
import { LayersContextProvider } from '../../contexts/Layers'
import { GeoJSONData, cadastralPlotsToGeoJSONData } from '../../utils/cartography'
import { domains } from '../../config'
import { LayersControl } from './Controls/LayersControl'
import { TaxonomicPlotIntersectionLayer } from './Layers/TaxonomicPlotsIntersectionLayer'
import { removeDuplicateObjects } from '../../utils'

type Props = {
  auctions?: MapAuctionFragment[]
  disableFallback?: boolean
}
type GeoJSONCadastralPlot = MapCadastralPlotFragment & {
  geoJSON: GeoJSONData
}
type AuctionGroupProps = {
  auction: AuctionProperties
  cadastralPlots: GeoJSONCadastralPlot[]
}

const AuctionGroup: FC<AuctionGroupProps> = ({ cadastralPlots, auction }) => {
  const { selected, setSelected } = useContext(AuctionListContext)
  const pathOptions = useMemo(
    () => ({
      fillOpacity: 0,
      weight: selected === auction.id ? 6 : 4,
    }),
    [selected, auction]
  )
  const eventHandlers = useMemo(
    () => ({
      mouseover: () => setSelected(auction.id),
      mouseout: () => setSelected(null),
    }),
    [auction, setSelected]
  )

  const cadastralPlotsWithGeoJSONData = cadastralPlots.filter(
    ({ geoJSON: [bounds, geoJSON] }) => bounds && geoJSON.length
  )

  return (
    <>
      {cadastralPlotsWithGeoJSONData.map((cadastralPlot) => (
        <CadastralPlotsGroup
          key={cadastralPlot.id}
          {...cadastralPlot}
          auction={auction}
          pathOptions={pathOptions}
          eventHandlers={eventHandlers}
        />
      ))}
    </>
  )
}

const mapProps = {
  zoom: 6,
  center: domains.current.mapCoordinates,
}

const AuctionListMap: FC<Props> = ({ auctions = [], disableFallback }) => {
  const [mapBounds, polygons] = useMemo(() => {
    const cadastralPlots = auctions.flatMap(({ cadastralPlots }) => cadastralPlots)
    const [bounds] = cadastralPlotsToGeoJSONData(cadastralPlots)

    return [
      bounds,
      auctions.map(({ cadastralPlots, ...auction }) => ({
        auction,
        cadastralPlots: cadastralPlots.map((cadastralPlot) => ({
          ...cadastralPlot,
          geoJSON: cadastralPlotsToGeoJSONData([cadastralPlot]),
        })),
      })),
    ]
  }, [auctions])
  const deflateLayerKey = useMemo(() => auctions.map(({ id }) => id).join('/'), [auctions])
  const cadastralPlots = useMemo(
    () => removeDuplicateObjects(auctions.flatMap(({ cadastralPlots }) => cadastralPlots)),
    [auctions]
  )

  return (
    <LayersContextProvider>
      <Map {...mapProps} fallback={!disableFallback && !!auctions.length && !mapBounds}>
        <LayersControl />
        <FitMap bounds={mapBounds} />
        <DeflateLayer key={deflateLayerKey}>
          {polygons.map(({ auction, cadastralPlots }) => (
            <AuctionGroup key={auction.id} auction={auction} cadastralPlots={cadastralPlots} />
          ))}
        </DeflateLayer>
        <TaxonomicPlotIntersectionLayer cadastralPlots={cadastralPlots} labelsVisible />
      </Map>
    </LayersContextProvider>
  )
}

export default memo(AuctionListMap)
